/* eslint-disable no-restricted-imports*/
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Paper, Typography, Stack, Portal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MUISnackbar, { SnackbarProps } from '@mui/material/Snackbar';
import Box from '@mui/material/Box';

import IconButton from '../IconButton';
import SeverityIcon from '../../media/colored-icons/SeverityIcon';
import { createSx } from '../../utils';
import { SNACKBAR_SEVERITY } from './const';

export interface Props extends Omit<SnackbarProps, 'action'> {
  severity: SNACKBAR_SEVERITY;
  actionLabel?: string;
  onClick?: () => void;
  onClose: () => void;
}

const sx = createSx({
  paper: {
    display: 'flex',
    backgroundColor: 'neutral.containerHighest',
    minWidth: 280,
    borderLeft: 'solid 4px',
    borderRadius: 0,
    '&.error': {
      borderColor: 'error.main',
    },
    '&.warning': {
      borderColor: 'warning.main'
    },
    '&.success': {
      borderColor: 'success.main'
    },
    '&.info': {
      borderColor: 'info.main',
    }
  },
  content: {
    p: 2.5,
    display: 'flex',
    flex: 1
  },
  message: {
    wordBreak: 'break-word',
    color: 'neutral.onSurfaceVariant'
  },
});

const Snackbar = ({
  open,
  title,
  message,
  actionLabel,
  onClick,
  onClose,
  severity = SNACKBAR_SEVERITY.WARNING,
  ...props
}: Props) => {
  const { t } = useTranslation();

  const onActionClick = () => {
    if (onClick) onClick();
    onClose();
  };

  return (
    //TODO: Refactoring (Maksym Ryzhykov) SPRINT 186 - Remove Portal in scope [https://over-haul.atlassian.net/browse/OK-45495]
    <Portal>
      <MUISnackbar {...props} open={open} onClose={onClose} data-test-id='snackbar'>
        <Paper elevation={4} sx={sx.paper} className={severity}>
          <Stack sx={sx.content} direction='row' spacing={2}>
            <SeverityIcon severity={severity} />
            <Box>
              <Typography variant='subtitle2Bold' data-test-id='snackbar-title'>{title}</Typography>
              <Typography sx={sx.message} variant='body2' data-test-id='snackbar-message'>{message}</Typography>
              {actionLabel && <Link onClick={onActionClick}>
                <Typography variant='button' sx={{ mt: 1 }} data-test-id='snackbar-actionLabel'>{actionLabel}</Typography>
              </Link>}
            </Box>
          </Stack>
          <Box>
            <IconButton
              title={t('close')}
              onClick={onClose}
              data-test-id='snackbar-closeBtn'>
              <CloseIcon fontSize='medium' />
            </IconButton>
          </Box>
        </Paper>
      </MUISnackbar>
    </Portal>
  );
};

export default Snackbar;